// ---------------------------------------------------------------------------
// local-validator — Studio-side wrapper around `repo.validate(phase)`.
//
// Phase 2 Step 5.1 #1 migration per:
//   - `docs/agentic/architecture/kg-repository-common.md` §5.1 #1+#3
//   - `docs/agentic/architecture/kg-repository-usage.md` §2.2
//
// Design points:
//   - Pure function that accepts `CogoKnowledgeRepository | null` — caller
//     (React component) owns the Repository lifecycle via `useKg().repo`.
//     Matches the fetcher-injection pattern used by `@cogo/binder-loader`.
//   - No direct `@cogo/kg-browser` import — Repository is the insulation
//     boundary. If kg-browser's `applyShacl` signature changes, only
//     `LocalWasmRepository.validate` needs to know.
//   - Rules are NOT defined here — they live in `./shacl-rules.ts` and are
//     injected into the Repository at provider construction via
//     `MapBackedRuleRegistry`. This file only dispatches on phase.
//   - Ships as reference impl for `kg-repository-usage.md` §2.2 "Non-React
//     utility" pattern. No callers in Studio today; wiring into the
//     validation UI panel is follow-up work.
// ---------------------------------------------------------------------------

import type {
  CogoKnowledgeRepository,
  ValidationPhase,
  ValidationReport,
} from '@cogo/kg-repository'
import type { ShaclViolation } from '@cogo/kg-browser'
import type { ValidationStatus } from './types'

export interface LocalValidationResult {
  /** Overall status — "pass" if no `violation`-severity findings; "warn" if only warnings; "fail" if any violation. */
  status: ValidationStatus
  /** Number of rules evaluated. */
  totalRules: number
  /** Violations surfaced (includes warnings/info — filter by severity for UI). */
  violations: ShaclViolation[]
  /** Wall-clock run time in ms. */
  durationMs: number
  /** Whether this result came from the local KG path (false == skipped/server). */
  fromLocal: boolean
  /** Optional error if validation threw. */
  error?: string
}

/** Status from a `ValidationReport.violations` severity distribution. */
function statusFromReport(report: ValidationReport): ValidationStatus {
  const hasViolation = report.violations.some((v) => v.severity === 'violation')
  if (hasViolation) return 'fail'
  const hasWarning = report.violations.some((v) => v.severity === 'warning')
  if (hasWarning) return 'warn'
  return 'pass'
}

/**
 * Run local SHACL validation via the Repository.
 *
 * Caller contract (React):
 * ```tsx
 * const { repo } = useKg()
 * const result = await runLocalValidation(repo, 'structural')
 * ```
 *
 * Caller contract (tests): inject a mock `CogoKnowledgeRepository`.
 *
 * Returns an idle result when `repo` is null (flag OFF, init not ready, or
 * init failed). Callers rendering validation UI should treat `status:"idle"`
 * as "no local result available — rely on server".
 */
export async function runLocalValidation(
  repo: CogoKnowledgeRepository | null,
  phase: ValidationPhase = 'structural',
): Promise<LocalValidationResult> {
  if (!repo) {
    return {
      status: 'idle',
      totalRules: 0,
      violations: [],
      durationMs: 0,
      fromLocal: false,
    }
  }

  try {
    const report = await repo.validate(phase)
    return {
      status: statusFromReport(report.data),
      totalRules: report.data.totalRules,
      violations: report.data.violations as ShaclViolation[],
      durationMs: report.meta.durationMs,
      fromLocal: report.meta.source === 'local',
    }
  } catch (err) {
    return {
      status: 'idle',
      totalRules: 0,
      violations: [],
      durationMs: 0,
      fromLocal: false,
      error: err instanceof Error ? err.message : String(err),
    }
  }
}

/**
 * Helper for callers that want to merge a local result into the existing
 * ValidationSummary shape. Filters severity buckets so the top-level
 * badge counts match UI expectations.
 */
export function summarizeLocalViolations(
  result: LocalValidationResult,
): { pass: number; warn: number; fail: number } {
  let pass = 0
  let warn = 0
  let fail = 0
  for (const v of result.violations) {
    if (v.severity === 'violation') fail += 1
    else if (v.severity === 'warning') warn += 1
    else if (v.severity === 'info') pass += 1
  }
  // Implicit passes: rules evaluated but not in violations list → counted as pass.
  const evaluatedButPassed = Math.max(0, result.totalRules - result.violations.length)
  pass += evaluatedButPassed
  return { pass, warn, fail }
}
